import React, { useId } from 'react';
import { m, Variants } from 'framer-motion';
import BrandMotion from './BrandMotion';

/**
 * The KoMold mark: an injection mold drawn in isometric, split the way a real
 * tool is — a clamping plate on top, a cavity half and a core half meeting at
 * the parting line, all seated on a bolster that overhangs the block.
 *
 * The overhang and the split lines are what keep this from reading as a plain
 * cube: they are the silhouette cues that say "assembly", not "box".
 *
 * An M is engraved across the parting line, half on each mold half, projected
 * onto each face's own basis so it sits *on* the steel rather than floating
 * over it. Neither half carries a readable letter alone — it only resolves
 * once the tool is closed.
 *
 * Every part is its own <m.g> bound to a variant, so the splash can
 * assemble them while every other screen renders this same file at rest.
 */

export type LogoState = 'blueprint' | 'assembled';

const wireframe: Variants = {
    blueprint: { pathLength: 0, opacity: 0 },
    assembled: {
        pathLength: [0, 1, 1],
        opacity: [0.85, 0.85, 0],
        transition: { duration: 0.5, times: [0, 0.5, 1], ease: 'easeInOut' },
    },
};

/** Parts travel in from the direction they would be fitted from. */
const slideY = (from: number, delay: number): Variants => ({
    blueprint: { y: from, opacity: 0 },
    assembled: {
        y: 0,
        opacity: 1,
        transition: { duration: 0.34, delay, ease: [0.22, 1, 0.36, 1] },
    },
});

const slideX = (from: number, delay: number): Variants => ({
    blueprint: { x: from, opacity: 0 },
    assembled: {
        x: 0,
        opacity: 1,
        transition: { duration: 0.34, delay, ease: [0.22, 1, 0.36, 1] },
    },
});

const plate = slideY(-18, 0.31);
const cavity = slideX(-16, 0.25);
const core = slideX(16, 0.25);
const base = slideY(14, 0.37);

/** The engraving only exists once both halves have closed on it. */
const engraving: Variants = {
    blueprint: { opacity: 0 },
    assembled: { opacity: 1, transition: { duration: 0.2, delay: 0.55 } },
};

/** A machined-surface highlight, once across the finished block. */
const sweep: Variants = {
    blueprint: { x: -78 },
    assembled: { x: 78, transition: { duration: 0.32, delay: 0.66, ease: 'easeInOut' } },
};

interface KoMoldLogoProps {
    className?: string;
    /**
     * Drive the assembly. Omit for the still, assembled mark — what the
     * sidebar, the login card and every loading state want.
     */
    state?: LogoState;
    /** Supply to expose the mark to assistive tech; otherwise it is decorative. */
    title?: string;
}

const KoMoldLogo: React.FC<KoMoldLogoProps> = ({ className, state, title }) => {
    // Ids must be unique per instance — two logos on one page would otherwise
    // share (and fight over) the same mask.
    const maskId = `komold-mask-${useId()}`;
    const sweepId = `komold-sweep-${useId()}`;
    const animating = state !== undefined;

    return (
        <BrandMotion>
            <m.svg
                viewBox="0 0 64 64"
                className={className}
                fill="none"
                role={title ? 'img' : undefined}
                aria-hidden={title ? undefined : true}
                aria-label={title}
                initial={animating ? 'blueprint' : false}
                animate={animating ? state : 'assembled'}
            >
                {title && <title>{title}</title>}

                {/* Construction lines: the tool's edges, struck before the steel arrives. */}
                <g stroke="#7FA6E0" strokeWidth="0.9" strokeLinejoin="round" fill="none">
                    <m.path variants={wireframe} d="M32 7 L53 18.5 L32 30 L11 18.5 Z" />
                    <m.path variants={wireframe} d="M11 21.5 L32 33 L32 53 L11 41.5 Z" />
                    <m.path variants={wireframe} d="M53 21.5 L32 33 L32 53 L53 41.5 Z" />
                    <m.path variants={wireframe} d="M8.5 41.5 L32 54.4 L55.5 41.5" />
                    <m.path variants={wireframe} d="M32 33 L32 53" strokeDasharray="2 2" />
                </g>

                {/* Bolster. Drawn first so the block covers all but the overhanging rim. */}
                <m.g variants={base}>
                    <path d="M32 30 L55.5 41.5 L32 54.4 L8.5 41.5 Z" fill="#2F5A9C" />
                    <path d="M8.5 41.5 L32 54.4 L32 57.9 L8.5 45 Z" fill="#274C86" />
                    <path d="M55.5 41.5 L32 54.4 L32 57.9 L55.5 45 Z" fill="#1F3E6E" />
                </m.g>

                {/* Cavity half, carrying the left stroke of the M. */}
                <m.g variants={cavity}>
                    <path d="M11 21.5 L32 33 L32 53 L11 41.5 Z" fill="#4C86D4" />
                    <m.path
                        variants={engraving}
                        d="M22.55 43.83 L22.55 33.43 L32 44"
                        stroke="#EAF2FD"
                        strokeOpacity="0.46"
                        strokeWidth="2.1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </m.g>

                {/* Core half, carrying the right stroke. */}
                <m.g variants={core}>
                    <path d="M53 21.5 L32 33 L32 53 L53 41.5 Z" fill="#3567B2" />
                    <m.path
                        variants={engraving}
                        d="M32 44 L41.45 33.43 L41.45 43.83"
                        stroke="#EAF2FD"
                        strokeOpacity="0.34"
                        strokeWidth="2.1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </m.g>

                {/* The parting line the two halves close on. */}
                <m.path
                    variants={engraving}
                    d="M32 33 L32 53"
                    stroke="#EAF2FD"
                    strokeOpacity="0.55"
                    strokeWidth="0.7"
                />

                {/* Clamping plate: face plus its thickness, so it reads as a slab. */}
                <m.g variants={plate}>
                    <path d="M11 18.5 L32 30 L32 33 L11 21.5 Z" fill="#A6C6EE" />
                    <path d="M53 18.5 L32 30 L32 33 L53 21.5 Z" fill="#7FA6E0" />
                    <path d="M32 7 L53 18.5 L32 30 L11 18.5 Z" fill="#CFDFF6" />
                    <path
                        d="M11 21.5 L32 33 L53 21.5"
                        stroke="#274C86"
                        strokeOpacity="0.40"
                        strokeWidth="0.7"
                        fill="none"
                    />
                </m.g>

                {/* Light sweep. Splash only — it has nothing to say at rest. */}
                {animating && (
                    <>
                        <defs>
                            <mask id={maskId}>
                                <path d="M32 7 L53 18.5 L53 21.5 L55.5 41.5 L55.5 45 L32 57.9 L8.5 45 L8.5 41.5 L11 21.5 L11 18.5 Z" fill="#fff" />
                            </mask>
                            <linearGradient id={sweepId} x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#EAF2FD" stopOpacity="0" />
                                <stop offset="50%" stopColor="#EAF2FD" stopOpacity="0.5" />
                                <stop offset="100%" stopColor="#EAF2FD" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <g mask={`url(#${maskId})`}>
                            <m.rect
                                variants={sweep}
                                x="-16"
                                y="-8"
                                width="26"
                                height="80"
                                fill={`url(#${sweepId})`}
                                transform="rotate(14 32 32)"
                            />
                        </g>
                    </>
                )}
            </m.svg>
        </BrandMotion>
    );
};

export default KoMoldLogo;
